/**
 * Debug utility to inspect IndexedDB contents
 * Use this in the browser console to see what's actually stored
 */

const DB_NAME = "spinesim-report";
const DB_VERSION = 2;
const STORE_NAME = "report-records";

/**
 * Get all records from IndexedDB
 * Usage in console: debugIndexedDB.getAllRecords().then(console.log)
 */
export async function getAllRecords() {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);

    request.onerror = () => reject(request.error);

    request.onsuccess = () => {
      const db = request.result;
      const transaction = db.transaction(STORE_NAME, "readonly");
      const store = transaction.objectStore(STORE_NAME);
      const getAllRequest = store.getAll();

      getAllRequest.onsuccess = () => {
        const records = getAllRequest.result;
        records.forEach((record, index) => {});

        db.close();
        resolve(records);
      };

      getAllRequest.onerror = () => reject(getAllRequest.error);
    };
  });
}

/**
 * Get records for the current session
 * Usage in console: debugIndexedDB.getCurrentSessionRecords().then(console.log)
 */
export async function getCurrentSessionRecords() {
  const sessionId = window.sessionStorage.getItem("spinesim-report-session-id");
  if (!sessionId) {
    return [];
  }

  const allRecords = await getAllRecords();
  const sessionRecords = allRecords.filter(r => r.sessionId === sessionId);

  return sessionRecords;
}

/**
 * Count records by section
 * Usage in console: debugIndexedDB.countBySection().then(console.log)
 */
export async function countBySection() {
  const records = await getAllRecords();
  const counts = {};

  records.forEach(record => {
    const section = record.section || "unknown";
    counts[section] = (counts[section] || 0) + 1;
  });

  return counts;
}

/**
 * Get database info
 * Usage in console: debugIndexedDB.getDatabaseInfo().then(console.log)
 */
export async function getDatabaseInfo() {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);

    request.onerror = () => reject(request.error);

    request.onsuccess = () => {
      const db = request.result;
      const info = {
        name: db.name,
        version: db.version,
        objectStoreNames: Array.from(db.objectStoreNames),
      };

      const transaction = db.transaction(STORE_NAME, "readonly");
      const store = transaction.objectStore(STORE_NAME);
      const countRequest = store.count();

      countRequest.onsuccess = () => {
        info.recordCount = countRequest.result;
        db.close();
        resolve(info);
      };

      countRequest.onerror = () => {
        db.close();
        reject(countRequest.error);
      };
    };
  });
}

/**
 * View a specific record's blob as an image
 * Usage in console: debugIndexedDB.viewRecordBlob('record-id-here')
 */
export async function viewRecordBlob(recordId) {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);

    request.onerror = () => reject(request.error);

    request.onsuccess = () => {
      const db = request.result;
      const transaction = db.transaction(STORE_NAME, "readonly");
      const store = transaction.objectStore(STORE_NAME);
      const getRequest = store.get(recordId);

      getRequest.onsuccess = () => {
        const record = getRequest.result;

        if (!record) {
          db.close();
          resolve(null);
          return;
        }

        if (!record.blob) {
          db.close();
          resolve(null);
          return;
        }

        const url = URL.createObjectURL(record.blob);
        // Create a temporary image to view it
        const img = document.createElement("img");
        img.src = url;
        img.style.maxWidth = "500px";
        img.style.border = "2px solid #00ff00";
        img.onclick = () => {
          document.body.removeChild(img);
          URL.revokeObjectURL(url);
        };
        document.body.appendChild(img);

        db.close();
        resolve(record);
      };

      getRequest.onerror = () => {
        db.close();
        reject(getRequest.error);
      };
    };
  });
}

/**
 * Clear all records (use with caution!)
 * Usage in console: debugIndexedDB.clearAll()
 */
export async function clearAll() {
  // eslint-disable-next-line no-restricted-globals
  const confirmed = confirm(
    "⚠️ Are you sure you want to clear all report records from IndexedDB?"
  );
  if (!confirmed) {
    return;
  }

  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);

    request.onerror = () => reject(request.error);

    request.onsuccess = () => {
      const db = request.result;
      const transaction = db.transaction(STORE_NAME, "readwrite");
      const store = transaction.objectStore(STORE_NAME);
      const clearRequest = store.clear();

      clearRequest.onsuccess = () => {
        db.close();
        resolve();
      };

      clearRequest.onerror = () => {
        db.close();
        reject(clearRequest.error);
      };
    };
  });
}

// Make available globally for console access
if (typeof window !== "undefined") {
  window.debugIndexedDB = {
    getAllRecords,
    getCurrentSessionRecords,
    countBySection,
    getDatabaseInfo,
    viewRecordBlob,
    clearAll,
  };
}
